import { createAsyncThunk } from '@reduxjs/toolkit';
import { AgaveGlCode, AgaveProject, Role } from 'API';
import { CardGroup, V2ListCardGroupsByPaidolIdPayload } from 'CDK_API';
import { cardService } from 'app/services/card.service';
import { agaveService } from 'app/services/agave.service';
import { RootState } from 'app/store/rootReducer';
import { getGroups, clearGroups } from 'app/pages/store/groupsSlice';
import { setColumnFilters } from 'app/store/filtering/searchAndFilterSlice';
import { ColumnFilterMap, FilterOption } from 'util/filtering/models';
import {
  buildCardGroupColumnFiltersFromConfig,
  generateCardGroupFilterObject,
  CardGroupDynamicFilterOptions,
} from 'app/store/filtering/cardGroupFilterConfig';
import { searchFlexCodes, searchFlexGlCodes } from 'app/pages/store/flexCodingSlice';
import { AgaveSourceSystem } from 'app/services/agave/agaveConstants';
import { showQBDFullName } from 'app/pages/overview/tabs/transactions/utils';
import { hasErpCodingUI } from 'app/util/paidolAccountType';

// Helper to format Agave label - uses showQBDFullName for QBD clients (same as card group settings form)
const formatAgaveLabel = (item: AgaveGlCode | AgaveProject, isQBDClient: boolean): string => {
  // For QBD, use the same function as the card group settings form
  if (isQBDClient) {
    return showQBDFullName(item as AgaveGlCode) || item.name || item.id;
  }
  // For other ERPs, try fullName first, then name + number
  if (item.fullName) return item.fullName;
  if (item.name && item.number) return `${item.name} - (${item.number})`;
  return item.name || item.id;
};

// Fetch all groups (with visibility settings) via pagination, through the CDK cardService —
// the same endpoint the card-groups list uses. Card groups have no dedicated filter-options
// resolver (unlike cards/transactions), so the facets are derived from the list itself; this
// was the one operation still fetching that list from Amplify (a second endpoint for the same
// query, diverging auth from the list). The CDK v2ListCardGroupsByPaidolId selection already
// returns groupAdmins, paymentCards→paidolUsers, last4, and the visibility settings this
// builder reads, and it excludes soft-deleted groups server-side (so no deletedAt check here).
const fetchAllGroupsWithVisibility = async (paidolId: string): Promise<CardGroup[]> => {
  let nextToken: string | null | undefined = undefined;
  const allGroups: CardGroup[] = [];

  do {
    const payload: V2ListCardGroupsByPaidolIdPayload = await cardService.v2ListCardGroupsByPaidolId({
      paidolId,
      nextToken: nextToken ?? undefined,
      limit: 100,
    });

    const items = payload?.items ?? [];
    allGroups.push(...items.filter((group): group is CardGroup => !!group));

    nextToken = payload?.nextToken;
  } while (nextToken);

  return allGroups;
};

export const retrieveCardGroupsFilterOptions = createAsyncThunk(
  'searchAndFilter/cardGroups/retrieveFilterOptions',
  async ({ pageName, paidolId }: { pageName: string; paidolId: string }, { dispatch, getState }) => {
    const state = getState() as RootState;
    const selectedPaidol = state.userCompanies.selectedPaidol;
    const usesAgaveCoding = hasErpCodingUI(selectedPaidol);
    const userRole = state.userCompanies.userRole;
    const currentPaidolUser = state.userCompanies.paidolUsers.find((pu) => pu.paidol?.id === paidolId);
    const currentUserId = currentPaidolUser?.id;

    // Check if QBD client for proper label formatting
    const agaveAccount = state.cards?.agave?.agaveAccount;
    const isQBDClient = agaveAccount?.source_system?.slug === AgaveSourceSystem.QUICKBOOKS_DESKTOP;

    let projectOptions: FilterOption[] = [];
    let glCodeOptions: FilterOption[] = [];
    let groupAdminOptions: FilterOption[] = [];
    let cardholderOptions: FilterOption[] = [];
    let last4Options: FilterOption[] = [];

    // Fetch all groups with visibility settings
    const allGroups = await fetchAllGroupsWithVisibility(paidolId);

    // For group admins, filter to only their groups for cardholder options
    const isGroupAdmin = userRole === Role.GROUP_ADMINISTRATOR;
    const relevantGroups = isGroupAdmin
      ? allGroups.filter((group) =>
          group.groupAdmins?.items?.some((admin) => admin?.paidolUser?.id === currentUserId)
        )
      : allGroups;

    // Extract unique group admins
    const groupAdminMap = new Map<string, FilterOption>();
    allGroups.forEach((group) => {
      group.groupAdmins?.items?.forEach((admin) => {
        const paidolUser = admin?.paidolUser;
        if (paidolUser?.id) {
          const name =
            paidolUser.user?.first_name && paidolUser.user?.last_name
              ? `${paidolUser.user.first_name} ${paidolUser.user.last_name}`.trim()
              : paidolUser.email || paidolUser.id;
          groupAdminMap.set(paidolUser.id, { id: paidolUser.id, label: name });
        }
      });
    });
    groupAdminOptions = Array.from(groupAdminMap.values()).sort((a, b) => a.label.localeCompare(b.label));

    // Extract unique cardholders (only from relevant groups for group admins)
    const cardholderMap = new Map<string, FilterOption>();
    relevantGroups.forEach((group) => {
      group.paymentCards?.items?.forEach((card) => {
        card?.paidolUsers?.items?.forEach((userItem) => {
          const paidolUser = userItem?.paidolUser;
          if (paidolUser?.id) {
            const name =
              paidolUser.user?.first_name && paidolUser.user?.last_name
                ? `${paidolUser.user.first_name} ${paidolUser.user.last_name}`.trim()
                : paidolUser.email || paidolUser.id;
            cardholderMap.set(paidolUser.id, { id: paidolUser.id, label: name });
          }
        });
      });
    });
    cardholderOptions = Array.from(cardholderMap.values()).sort((a, b) => a.label.localeCompare(b.label));

    // Extract unique last4 from cards in relevant groups
    const last4Map = new Map<string, FilterOption>();
    relevantGroups.forEach((group) => {
      group.paymentCards?.items?.forEach((card) => {
        const last4 = card?.last4;
        if (last4) {
          last4Map.set(last4, { id: last4, label: last4 });
        }
      });
    });
    last4Options = Array.from(last4Map.values()).sort((a, b) => a.id.localeCompare(b.id));

    // Collect restricted GL code and project IDs from card groups with limited visibility
    // This applies to both Flex and Agave clients - we only show options that are restricted in at least one group
    const restrictedGlCodeIds = new Set<string>();
    const restrictedProjectIds = new Set<string>();

    allGroups.forEach((group) => {
      if (group.glCodeVisibilitySettings?.isAllAvailable === false) {
        group.glCodeVisibilitySettings.ids?.forEach((id) => {
          if (id) restrictedGlCodeIds.add(id);
        });
      }
      if (group.projectVisibilitySettings?.isAllAvailable === false) {
        group.projectVisibilitySettings.ids?.forEach((id) => {
          if (id) restrictedProjectIds.add(id);
        });
      }
    });

    // Fetch projects and GL codes based on client type, filtered to restricted IDs only
    if (!usesAgaveCoding) {
      // Flex client - fetch flex codes, then filter to restricted IDs
      if (restrictedGlCodeIds.size > 0 || restrictedProjectIds.size > 0) {
        try {
          const [projectsResult, glCodesResult] = await Promise.all([
            dispatch(searchFlexCodes({ paidolId, glCodeId: '', searchTerm: '', isSettingsRequest: true })),
            dispatch(searchFlexGlCodes({ paidolId, searchTerm: '', isSettingsRequest: true })),
          ]);

          const projects = (projectsResult.payload as { items?: Array<{ id: string; projectName?: string }> })
            ?.items;
          if (projects && Array.isArray(projects)) {
            projectOptions = projects
              .filter((p) => p.projectName && restrictedProjectIds.has(p.id))
              .map((p) => ({ id: p.id, label: p.projectName || p.id }))
              .sort((a, b) => a.label.localeCompare(b.label));
          }

          const glCodes = (glCodesResult.payload as { items?: Array<{ id: string; name?: string }> })?.items;
          if (glCodes && Array.isArray(glCodes)) {
            glCodeOptions = glCodes
              .filter((g) => g.name && restrictedGlCodeIds.has(g.id))
              .map((g) => ({ id: g.id, label: g.name || g.id }))
              .sort((a, b) => a.label.localeCompare(b.label));
          }
        } catch (error) {
          console.error('Error fetching flex filter options:', error);
        }
      }
    } else {
      // Agave client - fetch only the restricted GL codes/projects by ID
      if (restrictedGlCodeIds.size > 0 || restrictedProjectIds.size > 0) {
        try {
          const [glCodesResult, projectsResult] = await Promise.all([
            restrictedGlCodeIds.size > 0
              ? agaveService.v2SearchAgaveGLCodes({
                  input: {
                    filter: { paidolId, ids: Array.from(restrictedGlCodeIds) },
                    limit: restrictedGlCodeIds.size,
                  },
                })
              : Promise.resolve({ items: [] }),
            restrictedProjectIds.size > 0
              ? agaveService.v2SearchAgaveProjects({
                  input: {
                    filter: { paidolId, ids: Array.from(restrictedProjectIds) },
                    limit: restrictedProjectIds.size,
                  },
                })
              : Promise.resolve({ items: [] }),
          ]);

          const glCodes = (glCodesResult?.items || []) as AgaveGlCode[];
          glCodeOptions = glCodes
            .filter((g) => g.name || g.fullName)
            .map((g) => ({ id: g.id, label: formatAgaveLabel(g, isQBDClient) }))
            .sort((a, b) => a.label.localeCompare(b.label));

          const projects = (projectsResult?.items || []) as AgaveProject[];
          projectOptions = projects
            .filter((p) => p.name || p.fullName)
            .map((p) => ({ id: p.id, label: formatAgaveLabel(p, isQBDClient) }))
            .sort((a, b) => a.label.localeCompare(b.label));
        } catch (error) {
          console.error('Error fetching Agave filter options:', error);
        }
      }
    }

    const dynamicOptions: CardGroupDynamicFilterOptions = {
      projectName: projectOptions,
      glCode: glCodeOptions,
      groupAdmin: groupAdminOptions,
      cardholder: cardholderOptions,
      last4: last4Options,
    };

    const columnFilters = buildCardGroupColumnFiltersFromConfig(pageName, dynamicOptions);

    // Preserve existing selected values
    const searchAndFilter = state.searchAndFilter[pageName];
    const existingFilters = searchAndFilter?.columnFilters || {};

    const mergedFilters = Object.keys(columnFilters).reduce((acc, key) => {
      const newFilter = columnFilters[key];
      const existingFilter = existingFilters[key];

      acc[key] = {
        ...newFilter,
        selectedValues: existingFilter?.selectedValues || newFilter.selectedValues,
        active: existingFilter?.active ?? newFilter.active,
        rule: existingFilter?.rule || newFilter.rule,
      };
      return acc;
    }, {} as ColumnFilterMap);

    dispatch(setColumnFilters({ pageName, columnFilters: mergedFilters }));

    return { pageName };
  }
);

export const applyCardGroupsColumnFilters = createAsyncThunk(
  'searchAndFilter/cardGroups/applyColumnFilters',
  async (
    { pageName, columnFilters }: { pageName: string; columnFilters: ColumnFilterMap },
    { dispatch, getState }
  ) => {
    const state = getState() as RootState;
    const paidolId = state.userCompanies.selectedPaidolId;

    if (!paidolId) {
      return;
    }

    dispatch(setColumnFilters({ pageName, columnFilters }));

    const filter = generateCardGroupFilterObject(columnFilters);
    const searchAndFilter = state.searchAndFilter[pageName];
    const sort = searchAndFilter?.sortOption?.field ? searchAndFilter.sortOption : undefined;

    dispatch(clearGroups());

    dispatch(
      getGroups({
        paidolId,
        limit: 100,
        sort,
        filter,
      })
    );

    return { pageName, columnFilters };
  }
);

export const applyCardGroupsSortFilter = createAsyncThunk(
  'searchAndFilter/cardGroups/applySortFilter',
  async (
    { pageName, sortOption }: { pageName: string; sortOption: { field: string; direction: string } },
    { dispatch, getState }
  ) => {
    const state = getState() as RootState;
    const paidolId = state.userCompanies.selectedPaidolId;

    if (!paidolId) {
      return;
    }

    const columnFilters = state.searchAndFilter[pageName]?.columnFilters || {};
    const filter = generateCardGroupFilterObject(columnFilters);

    dispatch(clearGroups());

    dispatch(
      getGroups({
        paidolId,
        limit: 100,
        sort: sortOption,
        filter,
      })
    );

    return { pageName, sortOption };
  }
);

export const clearCardGroupsColumnFilters = createAsyncThunk(
  'searchAndFilter/cardGroups/clearColumnFilters',
  async ({ pageName }: { pageName: string }, { dispatch, getState }) => {
    const state = getState() as RootState;
    const paidolId = state.userCompanies.selectedPaidolId;

    if (!paidolId) {
      return;
    }

    dispatch(clearGroups());

    const searchAndFilter = state.searchAndFilter[pageName];
    const sort = searchAndFilter?.sortOption?.field ? searchAndFilter.sortOption : undefined;

    dispatch(
      getGroups({
        paidolId,
        limit: 100,
        sort,
      })
    );

    return { pageName };
  }
);
